import { createStart, createMiddleware } from "@tanstack/react-start";

import { renderErrorPage } from "./lib/error-page";
import { sentryFunctionMiddleware } from "./lib/sentry-server";
import { attachSupabaseAuth } from "@/integrations/supabase/auth-attacher";
import { supabase } from "@/integrations/supabase/client";
import { handleInvalidSession, isExpiredSession } from "@/lib/auth/invalid-session";
import { getLocalSessionId } from "@/lib/auth/session-id";
import { XENO_SESSION_HEADER } from "@/lib/auth/session-header";

const errorMiddleware = createMiddleware().server(async ({ next }) => {
  try {
    return await next();
  } catch (error) {
    if (error != null && typeof error === "object" && "statusCode" in error) {
      throw error;
    }
    console.error(error);
    return new Response(renderErrorPage(), {
      status: 500,
      headers: { "content-type": "text/html; charset=utf-8" },
    });
  }
});

const ensureFreshSupabaseSession = createMiddleware({ type: "function" }).client(async ({ next }) => {
  if (typeof window === "undefined") return next();

  const { data: sessionData } = await supabase.auth.getSession();
  const session = sessionData.session;

  // No session at all → don't fire an unauthenticated RPC that will 401 on the
  // server and crash background queries mounted just before sign-out/navigation.
  if (!session) {
    await handleInvalidSession();
    throw new Error("Unauthorized: No active session");
  }

  if (!isExpiredSession(session)) return next();

  const { data: refreshed, error } = await supabase.auth.refreshSession();
  if (error || !refreshed.session) {
    await handleInvalidSession();
    throw new Error("Unauthorized: Invalid token");
  }

  return next();
});

const attachXenoSession = createMiddleware({ type: "function" }).client(async ({ next }) => {
  const id = getLocalSessionId();
  return next({ headers: id ? { [XENO_SESSION_HEADER]: id } : {} });
});

export const startInstance = createStart(() => ({
  functionMiddleware: [sentryFunctionMiddleware, ensureFreshSupabaseSession, attachSupabaseAuth, attachXenoSession],
  requestMiddleware: [errorMiddleware],
}));
